// src/utils/pdfChartIntegration.js
import Chart from 'chart.js/auto';

/**
 * PDF Chart Integration utility for embedding charts in PDF reports
 * Converts Chart.js configurations to canvas and embeds them in PDF
 */
export class PDFChartIntegration {
  constructor(pdfGenerator) {
    this.pdf = pdfGenerator;
    this.chartCanvas = null;
    this.chartContext = null;
    this.initializeCanvas();
  }

  /**
   * Initialize hidden canvas for chart rendering
   */
  initializeCanvas() {
    // Create hidden canvas for chart rendering
    this.chartCanvas = document.createElement('canvas');
    this.chartCanvas.style.display = 'none';
    this.chartCanvas.width = 800;
    this.chartCanvas.height = 600;
    document.body.appendChild(this.chartCanvas);
    this.chartContext = this.chartCanvas.getContext('2d');
  }

  /**
   * Add risk distribution pie chart to PDF
   * @param {Object} chartData - Chart data from chartService
   * @param {Object} options - Chart options
   */
  async addRiskDistributionChart(chartData, options = {}) {
    if (chartData.isEmpty) {
      this.pdf.addInfoBox('Graphique indisponible', chartData.emptyMessage, { type: 'warning' });
      return;
    }

    const { width = 80, height = 60, title = chartData.title, centered = false } = options;

    // Add chart title
    if (title) {
      this.pdf.addSectionHeading(title, 3);
    }

    try {
      // Configure Chart.js for PDF rendering
      const config = {
        type: 'pie',
        data: {
          labels: chartData.data.map(item => item.label),
          datasets: [{
            data: chartData.data.map(item => item.value),
            backgroundColor: chartData.data.map(item => item.color),
            borderColor: chartData.data.map(item => item.color),
            borderWidth: 2
          }]
        },
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: {
              position: 'right',
              labels: {
                usePointStyle: true,
                font: { size: 12 },
                generateLabels: (chart) => {
                  const data = chart.data;
                  const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                  return data.labels.map((label, i) => {
                    const value = data.datasets[0].data[i];
                    const percentage = ((value / total) * 100).toFixed(1);
                    return {
                      text: `${label}: ${value} (${percentage}%)`,
                      fillStyle: data.datasets[0].backgroundColor[i],
                      strokeStyle: data.datasets[0].borderColor[i],
                      pointStyle: 'circle'
                    };
                  });
                }
              }
            },
            tooltip: { enabled: false }
          }
        }
      };

      const chartImage = await this.renderChartToImage(config, width * 4, height * 4);
      this.pdf.checkPageBreak(height + 20);

      // Calculate x position - centered or left-aligned
      const xPosition = centered
        ? (this.pdf.pageWidth - width) / 2
        : this.pdf.margin;

      // Add chart to PDF
      this.pdf.pdf.addImage(chartImage, 'PNG', xPosition, this.pdf.currentY, width, height);
      this.pdf.currentY += height + 10;

      // Add chart explanation if available
      if (chartData.explanation) {
        this.pdf.addParagraph(chartData.explanation, { fontSize: 9, color: '#64748b' });
      }

    } catch (error) {
      console.error('Error adding risk distribution chart:', error);
      this.pdf.addInfoBox('Erreur de g\u00e9n\u00e9ration', 'Impossible de g\u00e9n\u00e9rer le graphique de distribution des risques', { type: 'danger' });
    }
  }

  /**
   * Add bar chart to PDF
   * @param {Object} chartData - Chart data from chartService
   * @param {Object} options - Chart options
   */
  async addBarChart(chartData, options = {}) {
    if (chartData.isEmpty) {
      this.pdf.addInfoBox('Graphique indisponible', chartData.emptyMessage, { type: 'warning' });
      return;
    }

    const { width = 120, height = 70, title = chartData.title } = options;

    if (title) {
      this.pdf.addSectionHeading(title, 3);
    }

    try {
      const config = {
        type: chartData.type === 'horizontalBar' ? 'bar' : chartData.type,
        data: chartData.data,
        options: {
          responsive: false,
          animation: false,
          indexAxis: chartData.type === 'horizontalBar' ? 'y' : 'x',
          plugins: {
            legend: {
              display: chartData.options?.plugins?.legend?.display !== false,
              position: 'top',
              labels: { font: { size: 11 } }
            },
            tooltip: { enabled: false }
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: { font: { size: 10 } },
              title: {
                display: !!chartData.options?.scales?.x?.title?.text,
                text: chartData.options?.scales?.x?.title?.text || '',
                font: { size: 11 }
              }
            },
            y: {
              beginAtZero: true,
              ticks: { font: { size: 10 } },
              title: {
                display: !!chartData.options?.scales?.y?.title?.text,
                text: chartData.options?.scales?.y?.title?.text || '',
                font: { size: 11 }
              }
            }
          }
        }
      };

      const chartImage = await this.renderChartToImage(config, width * 4, height * 4);
      this.pdf.checkPageBreak(height + 20);

      this.pdf.pdf.addImage(chartImage, 'PNG', this.pdf.margin, this.pdf.currentY, width, height);
      this.pdf.currentY += height + 10;

      if (chartData.explanation) {
        this.pdf.addParagraph(chartData.explanation, { fontSize: 9, color: '#64748b' });
      }

    } catch (error) {
      console.error('Error adding bar chart:', error);
      this.pdf.addInfoBox('Erreur de g\u00e9n\u00e9ration', 'Impossible de g\u00e9n\u00e9rer le graphique en barres', { type: 'danger' });
    }
  }

  /**
   * Add line chart to PDF
   * @param {Object} chartData - Chart data from chartService
   * @param {Object} options - Chart options
   */
  async addLineChart(chartData, options = {}) {
    if (chartData.isEmpty) {
      this.pdf.addInfoBox('Graphique indisponible', chartData.emptyMessage, { type: 'warning' });
      return;
    }

    const { width = 120, height = 70, title = chartData.title } = options;

    if (title) {
      this.pdf.addSectionHeading(title, 3);
    }

    try {
      const config = {
        type: 'line',
        data: chartData.data,
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: {
              display: chartData.data.datasets.length > 1,
              position: 'top',
              labels: { font: { size: 11 } }
            },
            tooltip: { enabled: false }
          },
          scales: {
            x: {
              ticks: { font: { size: 10 } },
              title: {
                display: !!chartData.options?.scales?.x?.title?.text,
                text: chartData.options?.scales?.x?.title?.text || '',
                font: { size: 11 }
              }
            },
            y: {
              beginAtZero: true,
              ticks: { font: { size: 10 } },
              title: {
                display: !!chartData.options?.scales?.y?.title?.text,
                text: chartData.options?.scales?.y?.title?.text || '',
                font: { size: 11 }
              }
            }
          },
          elements: {
            point: { radius: 4 },
            line: { tension: 0.3 }
          }
        }
      };

      const chartImage = await this.renderChartToImage(config, width * 4, height * 4);
      this.pdf.checkPageBreak(height + 20);

      this.pdf.pdf.addImage(chartImage, 'PNG', this.pdf.margin, this.pdf.currentY, width, height);
      this.pdf.currentY += height + 10;

      if (chartData.explanation) {
        this.pdf.addParagraph(chartData.explanation, { fontSize: 9, color: '#64748b' });
      }

    } catch (error) {
      console.error('Error adding line chart:', error);
      this.pdf.addInfoBox('Erreur de g\u00e9n\u00e9ration', 'Impossible de g\u00e9n\u00e9rer le graphique lin\u00e9aire', { type: 'danger' });
    }
  }

  /**
   * Add scatter plot to PDF
   * @param {Object} chartData - Chart data from chartService
   * @param {Object} options - Chart options
   */
  async addScatterPlot(chartData, options = {}) {
    if (chartData.isEmpty) {
      this.pdf.addInfoBox('Graphique indisponible', chartData.emptyMessage, { type: 'warning' });
      return;
    }

    const { width = 100, height = 80, title = chartData.title } = options;

    if (title) {
      this.pdf.addSectionHeading(title, 3);
    }

    try {
      const config = {
        type: 'scatter',
        data: chartData.data,
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false }
          },
          scales: {
            x: {
              type: 'linear',
              position: 'bottom',
              min: chartData.options?.scales?.x?.min || 1,
              max: chartData.options?.scales?.x?.max || 5,
              ticks: {
                stepSize: 1,
                font: { size: 10 },
                callback: function (value) {
                  const labels = ['', 'Tr\u00e8s faible', 'Faible', 'Moyen', '\u00c9lev\u00e9', 'Tr\u00e8s \u00e9lev\u00e9'];
                  return labels[value] || value;
                }
              },
              title: {
                display: true,
                text: chartData.options?.scales?.x?.title?.text || 'Vraisemblance',
                font: { size: 11 }
              }
            },
            y: {
              type: 'linear',
              min: chartData.options?.scales?.y?.min || 1,
              max: chartData.options?.scales?.y?.max || 5,
              ticks: {
                stepSize: 1,
                font: { size: 10 },
                callback: function (value) {
                  const labels = ['', 'Tr\u00e8s faible', 'Faible', 'Moyen', '\u00c9lev\u00e9', 'Critique'];
                  return labels[value] || value;
                }
              },
              title: {
                display: true,
                text: chartData.options?.scales?.y?.title?.text || 'Impact',
                font: { size: 11 }
              }
            }
          },
          elements: {
            point: { radius: 6 }
          }
        }
      };

      const chartImage = await this.renderChartToImage(config, width * 4, height * 4);
      this.pdf.checkPageBreak(height + 20);

      this.pdf.pdf.addImage(chartImage, 'PNG', this.pdf.margin, this.pdf.currentY, width, height);
      this.pdf.currentY += height + 10;

      if (chartData.explanation) {
        this.pdf.addParagraph(chartData.explanation, { fontSize: 9, color: '#64748b' });
      }

    } catch (error) {
      console.error('Error adding scatter plot:', error);
      this.pdf.addInfoBox('Erreur de g\u00e9n\u00e9ration', 'Impossible de g\u00e9n\u00e9rer le graphique de dispersion', { type: 'danger' });
    }
  }

  /**
   * Add donut chart to PDF
   * @param {Object} chartData - Chart data from chartService
   * @param {Object} options - Chart options
   */
  async addDonutChart(chartData, options = {}) {
    if (chartData.isEmpty) {
      this.pdf.addInfoBox('Graphique indisponible', chartData.emptyMessage, { type: 'warning' });
      return;
    }

    const { width = 80, height = 60, title = chartData.title } = options;

    if (title) {
      this.pdf.addSectionHeading(title, 3);
    }

    try {
      const config = {
        type: 'doughnut',
        data: chartData.data,
        options: {
          responsive: false,
          animation: false,
          cutout: chartData.options?.cutout || '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                usePointStyle: true,
                font: { size: 11 }
              }
            },
            tooltip: { enabled: false }
          }
        }
      };

      const chartImage = await this.renderChartToImage(config, width * 4, height * 4);
      this.pdf.checkPageBreak(height + 20);

      this.pdf.pdf.addImage(chartImage, 'PNG', this.pdf.margin, this.pdf.currentY, width, height);
      this.pdf.currentY += height + 10;

      // Add center text if available
      if (chartData.centerText) {
        const centerX = this.pdf.margin + width / 2;
        const centerY = this.pdf.currentY - height / 2;

        this.pdf.pdf.setFontSize(14);
        this.pdf.pdf.setTextColor('#1e40af');
        this.pdf.pdf.setFont('helvetica', 'bold');
        this.pdf.pdf.text(chartData.centerText.text, centerX, centerY, { align: 'center' });

        if (chartData.centerText.subtext) {
          this.pdf.pdf.setFontSize(10);
          this.pdf.pdf.setTextColor('#64748b');
          this.pdf.pdf.setFont('helvetica', 'normal');
          this.pdf.pdf.text(chartData.centerText.subtext, centerX, centerY + 5, { align: 'center' });
        }
      }

      if (chartData.explanation) {
        this.pdf.addParagraph(chartData.explanation, { fontSize: 9, color: '#64748b' });
      }

    } catch (error) {
      console.error('Error adding donut chart:', error);
      this.pdf.addInfoBox('Erreur de g\u00e9n\u00e9ration', 'Impossible de g\u00e9n\u00e9rer le graphique en anneau', { type: 'danger' });
    }
  }

  /**
   * Add radar chart to PDF
   * @param {Object} chartData - Chart data from chartService
   * @param {Object} options - Chart options
   */
  async addRadarChart(chartData, options = {}) {
    if (chartData.isEmpty) {
      this.pdf.addInfoBox('Graphique indisponible', chartData.emptyMessage, { type: 'warning' });
      return;
    }

    const { width = 90, height = 90, title = chartData.title } = options;

    if (title) {
      this.pdf.addSectionHeading(title, 3);
    }

    try {
      const config = {
        type: 'radar',
        data: chartData.data,
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: {
              display: chartData.data.datasets.length > 1,
              position: 'top',
              labels: { font: { size: 11 } }
            },
            tooltip: { enabled: false }
          },
          scales: {
            r: {
              beginAtZero: true,
              max: chartData.options?.scales?.r?.max || 100,
              ticks: {
                stepSize: chartData.options?.scales?.r?.ticks?.stepSize || 20,
                font: { size: 9 }
              },
              pointLabels: {
                font: { size: 10 }
              }
            }
          },
          elements: {
            point: { radius: 4 },
            line: { borderWidth: 2 }
          }
        }
      };

      const chartImage = await this.renderChartToImage(config, width * 4, height * 4);
      this.pdf.checkPageBreak(height + 20);

      this.pdf.pdf.addImage(chartImage, 'PNG', this.pdf.margin, this.pdf.currentY, width, height);
      this.pdf.currentY += height + 10;

      if (chartData.explanation) {
        this.pdf.addParagraph(chartData.explanation, { fontSize: 9, color: '#64748b' });
      }

    } catch (error) {
      console.error('Error adding radar chart:', error);
      this.pdf.addInfoBox('Erreur de g\u00e9n\u00e9ration', 'Impossible de g\u00e9n\u00e9rer le graphique radar', { type: 'danger' });
    }
  }

  /**
   * Add gauge chart to PDF (custom implementation)
   * @param {Object} chartData - Chart data from chartService
   * @param {Object} options - Chart options
   */
  async addGaugeChart(chartData, options = {}) {
    if (chartData.isEmpty) {
      this.pdf.addInfoBox('Graphique indisponible', chartData.emptyMessage, { type: 'warning' });
      return;
    }

    const { width = 80, height = 60, title = chartData.title } = options;

    if (title) {
      this.pdf.addSectionHeading(title, 3);
    }

    try {
      // Create custom gauge using canvas drawing
      const gaugeImage = await this.renderGaugeChart(chartData, width * 4, height * 4);
      this.pdf.checkPageBreak(height + 20);

      this.pdf.pdf.addImage(gaugeImage, 'PNG', this.pdf.margin, this.pdf.currentY, width, height);
      this.pdf.currentY += height + 10;

      // Add gauge value text
      if (chartData.centerText) {
        this.pdf.addParagraph(
          `${chartData.centerText.text}: ${chartData.centerText.subtext}`,
          { fontSize: 11, fontStyle: 'bold', alignment: 'center' }
        );
      }

      if (chartData.explanation) {
        this.pdf.addParagraph(chartData.explanation, { fontSize: 9, color: '#64748b' });
      }

    } catch (error) {
      console.error('Error adding gauge chart:', error);
      this.pdf.addInfoBox('Erreur de g\u00e9n\u00e9ration', 'Impossible de g\u00e9n\u00e9rer le graphique de jauge', { type: 'danger' });
    }
  }

  /**
   * Add comprehensive chart dashboard
   * @param {Object} chartData - All chart data from chartService
   */
  async addChartDashboard(chartData) {
    this.pdf.addSectionHeading('Tableaux de Bord et Visualisations', 1);
    this.pdf.addSpace(5);

    // Risk distribution section
    if (chartData.riskDistribution) {
      this.pdf.addSectionHeading('Distribution des Risques', 2);

      if (chartData.riskDistribution.severityPieChart) {
        await this.addRiskDistributionChart(chartData.riskDistribution.severityPieChart);
        this.pdf.addSpace(10);
      }

      if (chartData.riskDistribution.likelihoodBarChart) {
        await this.addBarChart(chartData.riskDistribution.likelihoodBarChart);
        this.pdf.addSpace(10);
      }

      if (chartData.riskDistribution.riskMatrixScatter) {
        await this.addScatterPlot(chartData.riskDistribution.riskMatrixScatter);
        this.pdf.addSpace(15);
      }
    }

    // Control effectiveness section
    if (chartData.controlEffectiveness) {
      this.pdf.addSectionHeading('Efficacit\u00e9 des Contr\u00f4les', 2);

      if (chartData.controlEffectiveness.implementationBarChart) {
        await this.addBarChart(chartData.controlEffectiveness.implementationBarChart);
        this.pdf.addSpace(10);
      }

      if (chartData.controlEffectiveness.coverageDonutChart) {
        await this.addDonutChart(chartData.controlEffectiveness.coverageDonutChart);
        this.pdf.addSpace(15);
      }
    }

    // Timeline section
    if (chartData.timeline) {
      this.pdf.addSectionHeading('Progression et Chronologie', 2);

      if (chartData.timeline.workshopProgressChart) {
        await this.addLineChart(chartData.timeline.workshopProgressChart);
        this.pdf.addSpace(10);
      }

      if (chartData.timeline.completionGaugeChart) {
        await this.addGaugeChart(chartData.timeline.completionGaugeChart);
        this.pdf.addSpace(15);
      }
    }

    // Business value section
    if (chartData.businessValue) {
      this.pdf.addSectionHeading('Analyse des Valeurs M\u00e9tier', 2);

      if (chartData.businessValue.criticalityDistributionChart) {
        await this.addRiskDistributionChart(chartData.businessValue.criticalityDistributionChart);
        this.pdf.addSpace(10);
      }

      if (chartData.businessValue.protectionCoverageChart) {
        await this.addBarChart(chartData.businessValue.protectionCoverageChart);
        this.pdf.addSpace(15);
      }
    }

    // Health metrics section
    if (chartData.healthMetrics) {
      this.pdf.addSectionHeading('M\u00e9triques de Sant\u00e9 de l\'Analyse', 2);

      if (chartData.healthMetrics.healthScoreRadarChart) {
        await this.addRadarChart(chartData.healthMetrics.healthScoreRadarChart);
        this.pdf.addSpace(10);
      }

      if (chartData.healthMetrics.maturityGaugeChart) {
        await this.addGaugeChart(chartData.healthMetrics.maturityGaugeChart);
        this.pdf.addSpace(15);
      }
    }
  }

  /**
   * Render Chart.js configuration to image
   * @param {Object} config - Chart.js configuration
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   * @returns {Promise<string>} Base64 image data
   */
  async renderChartToImage(config, width = 800, height = 600) {
    return new Promise((resolve, reject) => {
      try {
        // Set canvas dimensions
        this.chartCanvas.width = width;
        this.chartCanvas.height = height;

        // Create chart
        const chart = new Chart(this.chartContext, config);

        // Wait for chart to render
        setTimeout(() => {
          try {
            const imageData = this.chartCanvas.toDataURL('image/png');
            chart.destroy(); // Clean up
            resolve(imageData);
          } catch (error) {
            chart.destroy();
            reject(error);
          }
        }, 100);

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Render custom gauge chart
   * @param {Object} chartData - Gauge chart data
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   * @returns {Promise<string>} Base64 image data
   */
  async renderGaugeChart(chartData, width = 400, height = 300) {
    return new Promise((resolve) => {
      try {
        this.chartCanvas.width = width;
        this.chartCanvas.height = height;

        const ctx = this.chartContext;
        const centerX = width / 2;
        const centerY = height * 0.7;
        const radius = Math.min(width, height) * 0.3;

        // Clear canvas
        ctx.clearRect(0, 0, width, height);

        // Draw gauge background
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, Math.PI, 2 * Math.PI);
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 20;
        ctx.stroke();

        // Draw gauge segments
        const segments = chartData.data.segments || [];
        const totalAngle = Math.PI;
        let currentAngle = Math.PI;

        segments.forEach(segment => {
          const segmentAngle = (segment.max - segment.min) / chartData.data.max * totalAngle;

          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + segmentAngle);
          ctx.strokeStyle = segment.color;
          ctx.lineWidth = 20;
          ctx.stroke();

          currentAngle += segmentAngle;
        });

        // Draw needle
        const value = chartData.data.value || 0;
        const needleAngle = Math.PI + (value / chartData.data.max) * Math.PI;
        const needleLength = radius * 0.8;

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(
          centerX + needleLength * Math.cos(needleAngle),
          centerY + needleLength * Math.sin(needleAngle)
        );
        ctx.strokeStyle = '#374151';
        ctx.lineWidth = 4;
        ctx.stroke();

        // Draw center circle
        ctx.beginPath();
        ctx.arc(centerX, centerY, 8, 0, 2 * Math.PI);
        ctx.fillStyle = '#374151';
        ctx.fill();

        // Draw value text
        ctx.font = `${width * 0.08}px Arial`;
        ctx.fillStyle = '#374151';
        ctx.textAlign = 'center';
        ctx.fillText(`${value}%`, centerX, centerY + radius * 0.5);

        const imageData = this.chartCanvas.toDataURL('image/png');
        resolve(imageData);

      } catch (error) {
        console.error('Error rendering gauge chart:', error);
        resolve(this.createErrorImage(width, height));
      }
    });
  }

  /**
   * Create error image for failed chart rendering
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   * @returns {string} Base64 image data
   */
  createErrorImage(width, height) {
    this.chartCanvas.width = width;
    this.chartCanvas.height = height;

    const ctx = this.chartContext;

    // Clear and fill with light gray
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, width, height);

    // Draw error message
    ctx.font = `${width * 0.05}px Arial`;
    ctx.fillStyle = '#6b7280';
    ctx.textAlign = 'center';
    ctx.fillText('Erreur de g\u00e9n\u00e9ration', width / 2, height / 2);
    ctx.fillText('du graphique', width / 2, height / 2 + width * 0.06);

    return this.chartCanvas.toDataURL('image/png');
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.chartCanvas && this.chartCanvas.parentNode) {
      this.chartCanvas.parentNode.removeChild(this.chartCanvas);
    }
  }
}

export default PDFChartIntegration;